/*
 * Platform Analytics Experience — redesigned individual platform analytics
 * page, adopting the Cross-Platform Analytics design language (hero pill,
 * EDEN command bar, command metric strip, skyscraper delivery chart,
 * SHERLOC intelligence, Pantheon source facts).
 *
 * Isolation contract (ADDITIVE_ONLY_EXISTING_ANALYTICS_PRESERVED):
 * - This component is a parallel experience. It does NOT touch
 *   ConnectedPlatformAnalyticsPage, ProgrammaticChannelAnalyticsPage,
 *   AnalyticsReportShell, or any stylesheet they load. Facebook Analytics,
 *   Instagram Analytics, and Cinema Analytics never render through here.
 * - Shared services are consumed import-only (analyticsToplineService,
 *   useResolvedAnalyticsTimeframe, analyticsReportUtils) — none of them is
 *   modified.
 *
 * Reporting surface:
 * - This page IS the reporting surface. No Looker Studio / Data Studio CTA
 *   is rendered from here and no embedded Looker viewer is mounted — the
 *   native modules below are what a viewer consumes.
 * - The governed report mapping itself is untouched: the advertiser's
 *   configured report link is still resolved (profile.candidateFields) so
 *   the Connected report panel can state whether one exists, and Looker
 *   remains reachable through internal operator tooling.
 *
 * Data honesty:
 * - Live mode (default) renders only values the governed topline service
 *   reported. The delivery chart draws a column per day the reporting source
 *   actually reported, and falls back to the window total (saying so) for a
 *   source that reports no days; nothing is interpolated, and an unreported
 *   day is never drawn as a zero.
 * - The labeled design preview (?fixture=…) is the only surface that renders
 *   a daily composition, and it carries a persistent design-fixture badge.
 * - Client-billed reporting basis only.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import CphereHeaderLogo from "../components/CphereHeaderLogo";
import AnalyticsHeroPlatformLogo from "../components/AnalyticsHeroPlatformLogo";
import AnalyticsAskEden from "../components/AnalyticsAskEden";
import { getFirstValidUrlEntry } from "../components/analyticsReportUtils";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import usePageAwareness from "../hooks/usePageAwareness";
import useResolvedAnalyticsTimeframe from "../hooks/useResolvedAnalyticsTimeframe";
import { getToplinePerformance } from "../services/analyticsToplineService";
import {
  GA4_MEASUREMENT_SOURCE,
  getGa4MeasurementTopline,
} from "../services/ga4MeasurementService";
import {
  resolvePlatformAnalyticsProfile,
  resolvePlatformMetric,
  formatPlatformMetricValue,
} from "../platformAnalytics/platformAnalyticsProfiles";
import { NOT_DECLARED } from "../platformAnalytics/reportingSourceReadiness";
import CanonicalDeliveryChart, {
  DeliveryChartLegend,
} from "../platformAnalytics/DeliveryChart";
import { buildPlatformDeliveryChartModel } from "../platformAnalytics/deliveryChartModel";
import {
  PLATFORM_ANALYTICS_FIXTURE_STATES,
  buildPlatformAnalyticsFixture,
} from "../platformAnalytics/platformAnalyticsFixtures";
import { buildPlatformAnalyticsViewModel } from "../platformAnalytics/platformAnalyticsViewModel";
import { reportingTimeZoneStatement, sourceCalendarQualification } from "../utils/reportingCalendar";
import usePlatformAudienceSynthesis from "../platformAnalytics/usePlatformAudienceSynthesis";
import AudienceSynthesisPanel from "../platformAnalytics/AudienceSynthesisPanel";
import {
  buildAudienceSynthesisViewModel,
  describeAudienceSynthesisForEden,
  describeAudienceSynthesisTimestamp,
  resolveAudienceRequestPlatform,
} from "../platformAnalytics/audienceSynthesis";
import { buildAnalyticsPlanContext } from "../utils/analyticsPlanIntelligence";
import {
  DEFAULT_PLATFORM_WINDOW,
  FULL_DELIVERY_WINDOW_KEY,
  FULL_DELIVERY_WINDOW_LABEL,
  PLATFORM_COMPARISON_MODES,
  PLATFORM_WINDOW_PRESETS,
  buildPlatformCampaignOptions,
  buildPlatformWindow,
} from "../platformAnalytics/platformAnalyticsWorkspace";
import {
  buildCampaignScopeFlightWindow,
  buildCampaignScopeFullDeliveryWindow,
  campaignDeliveryExtensionProvenance,
  campaignFullDeliveryProvenance,
  campaignNoDeliveryState,
  campaignSelectorLabel,
} from "../platformAnalytics/campaignReportingLifecycle";
import DistributionTransparencyPanel from "../components/DistributionTransparencyPanel";
import LiveMetaAdsLibrary from "../components/LiveMetaAdsLibrary";
import CreativePerformancePanel from "../creativePerformance/CreativePerformancePanel";
import useCreativePerformance from "../creativePerformance/useCreativePerformance";
import {
  CREATIVE_PERFORMANCE_STATE,
  buildCreativePerformanceViewModel,
} from "../creativePerformance/creativePerformanceModel";
import {
  buildGoogleAnalyticsAudienceDashboard,
  renderGoogleAnalyticsAudienceDashboard,
} from "../ga4Analytics/ga4Modules";
import "./PlatformAnalyticsExperience.css";

/*
 * Client role: the backend already restricts the resolution payload to
 * client-approved fields; this flag additionally suppresses superadmin-only
 * provenance internals in the panel.
 */
const AUDIENCE_SYNTHESIS_ROLE = "client";

const DEFAULT_PRODUCTION_BACKEND_URL = "https://cphere-backend.onrender.com";
const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === "production" ? DEFAULT_PRODUCTION_BACKEND_URL : "");
function classNames(...values) {
  return values.filter(Boolean).join(" ");
}

function formatFreshness(minutes) {
  /*
   * A source that declares no timestamp says so in the same words as the
   * fields beside it. A bare dash reads as a rendering failure, and no age is
   * inferred from the request clock to fill the gap.
   */
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) return NOT_DECLARED;
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / (24 * 60))}d ago`;
}

/*
 * Deterministic EDEN command grammar. Governed and side-effect free: a
 * command updates page state or answers from the current view model. No
 * free-form model call happens here.
 */
export function parsePlatformEdenCommand(rawText, { profile } = {}) {
  const text = String(rawText || "").toLowerCase().trim();
  if (!text || !profile) return null;
  const updates = {};
  let answer = null;

  const metricMatch = profile.chartMetricKeys
    .concat(profile.commandMetricKeys)
    .map((key) => resolvePlatformMetric(key))
    .filter(Boolean)
    .find((metric) => text.includes(metric.label.toLowerCase()));
  if (metricMatch && (text.includes("show") || text.includes("chart") || text.includes("switch"))) {
    if (profile.chartMetricKeys.includes(metricMatch.key)) {
      updates.metricKey = metricMatch.key;
    } else {
      answer = { kind: "metric_not_chartable", metricLabel: metricMatch.label };
    }
  }

  /*
   * "open the report" used to launch the Looker Studio viewer. That external
   * surface is no longer exposed from this page, so the command answers with
   * where the reporting now lives instead of quietly doing nothing.
   */
  if (text.includes("open") && text.includes("report")) {
    answer = { kind: "report_is_this_page" };
  }
  if (text.includes("reset")) {
    updates.reset = true;
  }
  if (text.includes("audience") || text.includes("synthesis")) {
    if (
      text.includes("when") ||
      text.includes("generated") ||
      text.includes("last updated") ||
      text.includes("how old")
    ) {
      answer = { kind: "audience_timestamp" };
    } else {
      answer = { kind: "audience_summary" };
    }
  } else if (text.includes("fresh") || text.includes("up to date") || text.includes("updated")) {
    answer = { kind: "freshness_question" };
  }

  if (!Object.keys(updates).length && !answer) return null;
  return { updates, answer };
}

/* ------------------------------------------------------------------ */
/* Delivery chart                                                      */
/* ------------------------------------------------------------------ */

/*
 * This page draws the CANONICAL Delivery chart — the same component Quantum's
 * Cross-Platform Analytics, the authenticated Cortex Platform Analytics page
 * and the public report render.
 *
 * It used to have a private renderer of its own: same bars, same gridlines,
 * same idea, different implementation. That is how a surface ends up without a
 * capability every other surface has — the observed Site Visits · GA4 overlay
 * had existed on the shared renderer for a week and could not reach this page
 * because this page was not the shared renderer. Rather than porting the
 * overlay into a second chart (and then porting the next one, and the one after
 * that), the page now hands the shared chart a model.
 *
 * What is left here is the ADAPTER: this page's governed view model, mapped
 * into the canonical model shape. It derives no value and formats nothing
 * itself — `buildPlatformDeliveryChartModel` and the governed platform-metric
 * formatter do both, so a number on this page reads exactly as the same number
 * reads on every other one.
 */
export function DeliveryChart({ chart, accent, platformKey, platformLabel }) {
  const model = buildPlatformDeliveryChartModel({
    chart,
    platform: {
      key: platformKey || chart?.platformKey || "platform",
      label: platformLabel || "Platform",
      color: accent,
    },
    formatValue: formatPlatformMetricValue,
    siteVisitsAvailable: Boolean(chart?.siteVisits?.available),
  });
  if (!model) return null;
  return (
    <>
      <CanonicalDeliveryChart model={model} />
      {/*
        The legend names every series the plot draws, in the canonical
        treatment: the platform's own swatch, and — when GA4 actually observed
        something on a date this chart draws — the amber "Site Visits · GA4"
        rule that the right-hand axis is scaled for. It is rendered from the
        SAME model, so the legend can never name a series the plot omitted.
      */}
      <div className="ppx-chart-legend">
        <DeliveryChartLegend model={model} />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function PlatformAnalyticsExperience({ platformKey }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = resolvePlatformAnalyticsProfile(platformKey);

  const [loadedTopline, setTopline] = useState(null);
  const [loadedToplineScope, setLoadedToplineScope] = useState(null);
  const [toplineError, setToplineError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasResolved, setHasResolved] = useState(false);
  const [activeAnalyticsAdvertiserId, setActiveAnalyticsAdvertiserId] =
    useState(null);
  const advertiserScopeEpochRef = useRef(0);
  const toplineRequestControllerRef = useRef(null);

  const [metricKey, setMetricKey] = useState(() => searchParams.get("metric") || "");
  const fixtureState = searchParams.get("fixture") || "";
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [describeOpen, setDescribeOpen] = useState(false);
  const [edenInput, setEdenInput] = useState("");
  const [edenAnswer, setEdenAnswer] = useState(null);

  const clearAdvertiserScopedPresentationState = useCallback(() => {
    advertiserScopeEpochRef.current += 1;
    toplineRequestControllerRef.current?.abort();
    toplineRequestControllerRef.current = null;
    setActiveAnalyticsAdvertiserId(null);
    setTopline(null);
    setToplineError(null);
    setLoading(false);
    setHasResolved(false);
    setDescribeOpen(false);
    setEdenAnswer(null);
  }, []);

  const {
    selectedAdvertiserId,
    visibleAdvertisers,
    currentAdvertiser,
    loading: advertiserCatalogLoading,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedPresentationState,
  });

  const resolvedAdvertiser = currentAdvertiser;
  const resolvedAdvertiserId = resolvedAdvertiser?.id ?? null;
  const resolvedAdvertiserName = String(
    resolvedAdvertiser?.name || ""
  ).trim();
  const governedAnalyticsAdvertiserId =
    activeAnalyticsAdvertiserId != null &&
    String(activeAnalyticsAdvertiserId) === String(resolvedAdvertiserId)
      ? resolvedAdvertiserId
      : null;

  useEffect(() => {
    setActiveAnalyticsAdvertiserId(resolvedAdvertiserId || null);
  }, [resolvedAdvertiserId]);

  /* URL state — shareable metric + explicit fixture preview only. */
  useEffect(() => {
    const next = new URLSearchParams();
    if (fixtureState) next.set("fixture", fixtureState);
    if (metricKey) next.set("metric", metricKey);
    setSearchParams(next, { replace: true });
  }, [fixtureState, metricKey, setSearchParams]);

  const setFixtureState = useCallback(
    (nextState) => {
      const next = new URLSearchParams();
      if (nextState) next.set("fixture", nextState);
      if (metricKey) next.set("metric", metricKey);
      setSearchParams(next, { replace: true });
    },
    [metricKey, setSearchParams]
  );

  /*
   * Report link resolution — same candidate policy as the legacy page, and
   * deliberately still resolved: the governed mapping between an advertiser
   * and its configured report is unchanged. What this page does with the
   * result is narrower than it used to be. Only the *existence* of a
   * configured report is read (reportAvailable); the URL itself is never
   * rendered, linked, or embedded here, because Looker Studio is no longer
   * the client-facing reporting experience — this page is.
   */
  const reportCandidateEntries = useMemo(() => {
    const raw = resolvedAdvertiser?.raw;
    if (!raw || typeof raw !== "object" || !profile) return [];
    const entries = profile.candidateFields.map((field) => ({
      label: `${profile.reportSourceLabel} • ${field}`,
      value: raw?.[field],
    }));
    if (profile.genericScan && profile.genericChannelKey) {
      Object.entries(raw).forEach(([key, value]) => {
        if (typeof value !== "string" || !value.trim()) return;
        const normalizedKey = key.toLowerCase();
        const matchesChannel = normalizedKey.includes(profile.genericChannelKey.toLowerCase());
        const matchesType = ["link", "url", "report", "looker", "analytics"].some((fragment) =>
          normalizedKey.includes(fragment)
        );
        if (matchesChannel && matchesType) {
          entries.push({ label: `${profile.reportSourceLabel} • ${key}`, value });
        }
      });
    }
    return entries;
  }, [profile, resolvedAdvertiser]);

  const reportAvailable = useMemo(
    () => Boolean(getFirstValidUrlEntry(reportCandidateEntries)),
    [reportCandidateEntries]
  );

  /* ------------------------------------------------------------- */
  /* Live topline                                                    */
  /* ------------------------------------------------------------- */

  const { advertiserFlightRange, timeframe: flightTimeframe, campaigns } =
    useResolvedAnalyticsTimeframe(
      fixtureState ? null : governedAnalyticsAdvertiserId,
      fixtureState ? null : resolvedAdvertiser?.raw
    );

  /* ------------------------------------------------------------- */
  /* Reporting window — the Platform Analytics control vocabulary   */
  /*                                                                */
  /* Campaign flight stays the governed default this page already   */
  /* resolves from the approved plan; the presets are the same four */
  /* Platform Analytics offers, resolved through the explicit       */
  /* override the topline contract already understands rather than  */
  /* a parallel request path.                                       */
  /* ------------------------------------------------------------- */

  const [rangeKey, setRangeKey] = useState(DEFAULT_PLATFORM_WINDOW);
  const [campaignId, setCampaignId] = useState("");
  const [comparison, setComparison] = useState("prior_period");

  const campaignOptions = useMemo(
    () => buildPlatformCampaignOptions(campaigns, profile?.platformKey),
    [campaigns, profile]
  );
  const selectedCampaignOption = useMemo(
    () => campaignOptions.find((campaign) => String(campaign.id) === String(campaignId)) || null,
    [campaignId, campaignOptions]
  );
  const campaignScopeFlightWindow = useMemo(
    () => buildCampaignScopeFlightWindow(campaignOptions, campaignId),
    [campaignId, campaignOptions]
  );
  const scopedFlightTimeframe = useMemo(
    () =>
      campaignScopeFlightWindow
        ? {
            ...(flightTimeframe || {}),
            datePreset: "flight",
            start: campaignScopeFlightWindow.startDate,
            end: campaignScopeFlightWindow.endDate,
            key: `flight:${campaignScopeFlightWindow.startDate}:${campaignScopeFlightWindow.endDate}`,
            source: "canonical_campaign_scope_flight",
            isExplicitOverride: false,
          }
        : flightTimeframe,
    [campaignScopeFlightWindow, flightTimeframe]
  );
  /*
   * The FULL DELIVERY window — the actual delivery lifecycle for the same
   * campaign scope. Offered only when it ends strictly later than the
   * campaign flight; otherwise the two views are identical and the extra
   * chip would be clutter.
   */
  const campaignScopeFullDeliveryWindow = useMemo(
    () => buildCampaignScopeFullDeliveryWindow(campaignOptions, campaignId),
    [campaignId, campaignOptions]
  );
  const fullDeliveryOffered = Boolean(
    campaignScopeFlightWindow &&
      campaignScopeFullDeliveryWindow &&
      campaignScopeFullDeliveryWindow.endDate > campaignScopeFlightWindow.endDate
  );
  const scopedFullDeliveryTimeframe = useMemo(
    () =>
      fullDeliveryOffered
        ? {
            ...(flightTimeframe || {}),
            datePreset: FULL_DELIVERY_WINDOW_KEY,
            start: campaignScopeFullDeliveryWindow.startDate,
            end: campaignScopeFullDeliveryWindow.endDate,
            key: `full_delivery:${campaignScopeFullDeliveryWindow.startDate}:${campaignScopeFullDeliveryWindow.endDate}`,
            source: "canonical_campaign_scope_full_delivery",
            isExplicitOverride: false,
          }
        : null,
    [campaignScopeFullDeliveryWindow, flightTimeframe, fullDeliveryOffered]
  );
  // A FULL DELIVERY selection whose window is no longer offered (scope
  // change, campaign records refresh) reads as the flight default — visibly,
  // on the chips, never silently on a stale window.
  const effectiveRangeKey =
    rangeKey === FULL_DELIVERY_WINDOW_KEY && !fullDeliveryOffered
      ? DEFAULT_PLATFORM_WINDOW
      : rangeKey;
  const resolvedWindow = useMemo(
    () =>
      buildPlatformWindow({
        rangeKey: effectiveRangeKey,
        flightTimeframe: scopedFlightTimeframe,
        fullDeliveryTimeframe: scopedFullDeliveryTimeframe,
      }),
    [effectiveRangeKey, scopedFlightTimeframe, scopedFullDeliveryTimeframe]
  );
  const timeframe = resolvedWindow.timeframe;
  const selectedCampaignNoDelivery = useMemo(
    () => campaignNoDeliveryState(selectedCampaignOption),
    [selectedCampaignOption]
  );
  /*
   * Provenance for a client-authorized Full Delivery extension — stated only
   * when the scope resolves to exactly one campaign, because the sentence
   * (original approved flight vs authorized delivery window) describes ONE
   * campaign's authorization, never a blend.
   */
  const deliveryExtensionProvenance = useMemo(() => {
    const scoped = campaignId
      ? campaignOptions.filter((campaign) => String(campaign.id) === String(campaignId))
      : campaignOptions;
    return scoped.length === 1
      ? campaignDeliveryExtensionProvenance(scoped[0])
      : null;
  }, [campaignId, campaignOptions]);
  /*
   * Provenance for the FULL DELIVERY view — same single-campaign rule: the
   * original approved flight, then either the authorized extension or the
   * governed admission that no authorization was recorded.
   */
  const fullDeliveryProvenance = useMemo(() => {
    const scoped = campaignId
      ? campaignOptions.filter((campaign) => String(campaign.id) === String(campaignId))
      : campaignOptions;
    return scoped.length === 1
      ? campaignFullDeliveryProvenance(scoped[0])
      : null;
  }, [campaignId, campaignOptions]);

  /*
   * Cortex never offers a spend-basis control. Platform Analytics does not
   * show one to a client either: gross (client-billed) is the only basis a
   * client is entitled to, and rendering a disabled "Net" chip would advertise
   * a distinction they are not party to. Quantum keeps the gated control.
   */

  /* ------------------------------------------------------------- */
  /* Audience Data Synthesis — canonical resolution, read-only      */
  /* ------------------------------------------------------------- */

  const planContext = useMemo(
    () =>
      buildAnalyticsPlanContext({
        campaigns,
        platformKey: profile?.platformKey,
        platformLabel: profile?.heroLabel,
      }),
    [campaigns, profile]
  );
  const preferredCampaign = planContext?.approvedCampaign || null;
  const audienceRequestPlatform = resolveAudienceRequestPlatform(profile);
  const { requestState: audienceRequestState, resolution: audienceResolution } =
    usePlatformAudienceSynthesis({
      advertiserId: fixtureState ? null : governedAnalyticsAdvertiserId,
      platform: audienceRequestPlatform,
      campaignId: preferredCampaign?.id || null,
      campaignName:
        preferredCampaign?.campaign_name || preferredCampaign?.campaignName || null,
      startDate: timeframe?.start || null,
      endDate: timeframe?.end || null,
      enabled: !fixtureState,
    });

  // Gate during render as well as cancelling in the effect: the first render
  // of a new campaign/platform/window must never expose the previous scope.
  const analyticsScopeKey = JSON.stringify([
    governedAnalyticsAdvertiserId, profile?.platformKey,
    selectedCampaignOption?.id || null, selectedCampaignOption?.name || null,
    timeframe, advertiserFlightRange,
  ]);
  const topline = loadedToplineScope === analyticsScopeKey ? loadedTopline : null;

  useEffect(() => {
    toplineRequestControllerRef.current?.abort();
    toplineRequestControllerRef.current = null;
    setTopline(null);
    setToplineError(null);
    setHasResolved(false);

    if (fixtureState || !governedAnalyticsAdvertiserId || !profile) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const scopeEpoch = advertiserScopeEpochRef.current;
    toplineRequestControllerRef.current = controller;
    setLoading(true);
    (async () => {
      try {
        /*
         * The one seam that makes this workspace source-agnostic. A
         * measurement profile fills the same governed payload shape from its
         * own contract; everything downstream — view model, KPI strip, chart,
         * window controls — is unchanged and cannot tell the difference.
         * Profiles without a metricsSource take the paid-media path exactly as
         * before.
         */
        const loadGovernedTopline =
          profile.metricsSource === GA4_MEASUREMENT_SOURCE
            ? getGa4MeasurementTopline
            : getToplinePerformance;
        const payload = await loadGovernedTopline({
          advertiserId: governedAnalyticsAdvertiserId,
          timeframe,
          advertiserFlightRange,
          platformKey: profile.platformKey,
          campaignId: selectedCampaignOption?.id || null,
          campaignName: selectedCampaignOption?.name || null,
          signal: controller.signal,
        });
        if (
          !cancelled &&
          !controller.signal.aborted &&
          scopeEpoch === advertiserScopeEpochRef.current
        ) {
          setLoadedToplineScope(analyticsScopeKey);
          setTopline(payload);
          setHasResolved(true);
          setLoading(false);
        }
      } catch (error) {
        if (
          !cancelled &&
          !controller.signal.aborted &&
          scopeEpoch === advertiserScopeEpochRef.current &&
          error?.name !== "AbortError"
        ) {
          setToplineError(error);
          setHasResolved(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      if (toplineRequestControllerRef.current === controller) {
        toplineRequestControllerRef.current = null;
      }
    };
  }, [
    analyticsScopeKey,
    advertiserFlightRange,
    fixtureState,
    governedAnalyticsAdvertiserId,
    profile,
    selectedCampaignOption,
    timeframe,
  ]);

  /* ------------------------------------------------------------- */
  /* Creative Performance                                            */
  /*                                                                 */
  /* Scoped to the same advertiser, platform and reporting window as  */
  /* the KPIs above it, so the creatives always describe the window   */
  /* the page is showing. Fixture mode never requests it: the design  */
  /* preview must not put invented creative results in front of a     */
  /* client.                                                          */
  /* ------------------------------------------------------------- */

  const [creativeSort, setCreativeSort] = useState(null);
  /*
   * GA4 measurement wiring. The daily series is no longer fetched here: the
   * measurement adapter reads it on the same resolved window as the aggregate
   * and publishes it as `dailyDelivery`, so the chart and the KPI cards cannot
   * disagree about the period, and this page has one request path per source
   * rather than a second one only GA4 takes.
   */
  const isGa4Measurement = profile?.metricsSource === GA4_MEASUREMENT_SOURCE;
  const ga4AudienceDashboard = useMemo(
    () =>
      isGa4Measurement && topline?.raw?.audienceChannelData
        ? buildGoogleAnalyticsAudienceDashboard(topline.raw.audienceChannelData)
        : null,
    [isGa4Measurement, topline]
  );

  const creativePlatformKey = profile?.creativePerformancePlatformKey || null;

  const { status: creativeStatus, projection: creativeProjection } = useCreativePerformance({
    advertiserId: fixtureState ? null : governedAnalyticsAdvertiserId,
    platformKey: creativePlatformKey,
    startDate: timeframe?.start || null,
    endDate: timeframe?.end || null,
    sort: creativeSort,
    enabled: Boolean(creativePlatformKey) && !fixtureState,
  });

  /*
   * Every platform page carries the Creative Performance section, including the
   * ones whose connector reports no creative-level delivery. Omitting it there
   * would make the page architecture differ by platform and, worse, would leave
   * the reader to guess whether creative results are missing or simply never
   * collected. A platform without a certified contract renders the governed
   * platform_unsupported state, which says the source does not report them —
   * a statement about the connector, not about the creatives.
   */
  const creativeViewModel = useMemo(
    () =>
      buildCreativePerformanceViewModel({
        projection: creativePlatformKey
          ? creativeProjection
          : {
              platformKey: profile?.platformKey || null,
              platformLabel: profile?.heroLabel || "",
              state: CREATIVE_PERFORMANCE_STATE.PLATFORM_UNSUPPORTED,
              reason: null,
              creatives: [],
              supportedMetrics: [],
              sortOptions: [],
            },
        status: creativePlatformKey ? creativeStatus : null,
        platformLabel: profile?.heroLabel || "",
        window: timeframe?.start && timeframe?.end
          ? { startDate: timeframe.start, endDate: timeframe.end }
          : null,
      }),
    [creativePlatformKey, creativeProjection, creativeStatus, profile, timeframe]
  );

  /* ------------------------------------------------------------- */
  /* View model                                                      */
  /* ------------------------------------------------------------- */

  const fixture = useMemo(
    () => (fixtureState && profile ? buildPlatformAnalyticsFixture(profile.platformKey, fixtureState) : null),
    [fixtureState, profile]
  );

  const viewModel = useMemo(
    () =>
      buildPlatformAnalyticsViewModel({
        mode: fixtureState ? "fixture" : "live",
        profile,
        topline: governedAnalyticsAdvertiserId ? topline : null,
        toplineError: governedAnalyticsAdvertiserId ? toplineError : null,
        fixture,
        selectedMetricKey: metricKey || null,
      }),
    [
      fixtureState,
      governedAnalyticsAdvertiserId,
      profile,
      topline,
      toplineError,
      fixture,
      metricKey,
    ]
  );

  const audienceViewModel = useMemo(() => {
    if (fixtureState) {
      const fixtureAudience = fixture?.audience;
      return buildAudienceSynthesisViewModel({
        resolution: fixtureAudience?.resolution || null,
        requestState: fixtureAudience?.requestState || "resolved",
        support: profile?.audienceSupport,
        role: AUDIENCE_SYNTHESIS_ROLE,
      });
    }
    return buildAudienceSynthesisViewModel({
      resolution: audienceResolution,
      requestState: audienceRequestState,
      support: profile?.audienceSupport,
      role: AUDIENCE_SYNTHESIS_ROLE,
    });
  }, [audienceRequestState, audienceResolution, fixture, fixtureState, profile]);

  const pageAwareness = useMemo(
    () => ({
      routeKey: profile?.routeKey || "",
      pageTitle: profile?.pageTitle || "Platform Analytics",
      platformKey: profile?.platformKey || "",
      pageCapabilities: profile?.pageCapabilities || [],
      pageToplineMetrics:
        viewModel.state === "ready"
          ? {
              spend: topline?.raw?.spend ?? null,
              impressions: topline?.raw?.impressions ?? null,
              clicks: topline?.raw?.clicks ?? null,
              sourceKind: viewModel.sourceKind,
            }
          : { sourceKind: viewModel.sourceKind },
    }),
    [profile, viewModel, topline]
  );
  usePageAwareness(pageAwareness);

  const submitEdenCommand = useCallback(
    (event) => {
      event.preventDefault();
      const parsed = parsePlatformEdenCommand(edenInput, { profile });
      if (!parsed) {
        setEdenAnswer(
          `EDEN could not map that to a governed ${profile?.heroLabel || "platform"} analytics command. ${
            profile?.edenExamples?.[0] || ""
          }`.trim()
        );
        return;
      }
      if (parsed.updates.metricKey) setMetricKey(parsed.updates.metricKey);
      if (parsed.updates.reset) {
        setMetricKey("");
        setFixtureState("");
      }
      if (parsed.answer?.kind === "report_is_this_page") {
        setEdenAnswer(
          `${profile?.heroLabel || "This platform"} reporting is on this page — delivery, creative performance and audience synthesis are all above. There is no separate report to open.`
        );
        setEdenInput("");
        return;
      }
      if (parsed.answer?.kind === "audience_summary") {
        setEdenAnswer(
          describeAudienceSynthesisForEden(audienceViewModel, { heroLabel: profile?.heroLabel })
        );
        setEdenInput("");
        return;
      }
      if (parsed.answer?.kind === "audience_timestamp") {
        setEdenAnswer(describeAudienceSynthesisTimestamp(audienceViewModel));
        setEdenInput("");
        return;
      }
      if (parsed.answer?.kind === "freshness_question") {
        const pantheon = viewModel.pantheon;
        setEdenAnswer(
          pantheon?.assessmentAvailable && pantheon.freshnessMinutes != null
            ? `The reporting source (${pantheon.sourceLabel || "unknown"}) last updated ${formatFreshness(
                pantheon.freshnessMinutes
              )}.`
            : "No freshness timestamp is declared by the current reporting source."
        );
      } else if (parsed.answer?.kind === "metric_not_chartable") {
        setEdenAnswer(
          `${parsed.answer.metricLabel} renders as a metric card — the delivery chart carries volume metrics only.`
        );
      } else if (parsed.updates.metricKey) {
        setEdenAnswer("Chart metric updated.");
      } else {
        setEdenAnswer("Filters updated.");
      }
      setEdenInput("");
    },
    [audienceViewModel, edenInput, profile, viewModel, setFixtureState]
  );

  /*
   * Context the ads library renders beside its live figures. Built from this
   * page's own governed view model rather than a second fetch, and null unless
   * the page actually resolved reporting — an empty frame would imply a
   * snapshot exists when none does.
   */
  const metaReportingSnapshot = useMemo(() => {
    if (!profile?.metaAdsLibrary || viewModel.state !== "ready") return null;
    return {
      campaignName: planContext?.approvedCampaign?.campaign_name || null,
      dateLabel: viewModel.window?.label || "Not available",
      sourceLabel: `Cphere reporting snapshot · ${
        viewModel.pantheon?.sourceLabel || "recorded/imported reporting"
      }`,
      summary: viewModel.chart?.textSummary || null,
      metrics: (viewModel.commandMetrics || [])
        .filter((metric) => metric.available)
        .map((metric) => ({ label: metric.label, value: metric.value })),
    };
  }, [profile, viewModel, planContext]);

  if (!profile) {
    return (
      <div className="core-dashboard ppx-page-container">
        <div className="core-dashboard-content ppx-page-content cortex-page-intro-shell">
          <div className="ppx-state-panel" role="alert">
            <h2 className="ppx-state-title">Unknown analytics platform</h2>
            <p className="ppx-state-body">This route is not registered with a platform analytics profile.</p>
          </div>
        </div>
      </div>
    );
  }

  const ready = viewModel.state === "ready";


  const chartMetric = resolvePlatformMetric(viewModel.chart?.metricKey);

  return (
    <div className="core-dashboard ppx-page-container" style={{ "--ppx-accent": profile.accent }}>
      <div className="core-dashboard-content ppx-page-content cortex-page-intro-shell">
        <CphereHeaderLogo
          alt="Powered by Logo"
          className="ppx-brand-logo"
          onClick={() => navigate("/dashboard")}
          style={{ cursor: "pointer" }}
        />

        <div className="cphere-governed-hero-selector-stack">
          <div className="ppx-hero-container">
            <div className="ppx-hero-pill">
              <AnalyticsHeroPlatformLogo platformKey={profile.platformKey} />
              {/* The hero is the ONE place the wordmark is read. `heroLabel`
                  is the semantic name every sentence and chart legend on this
                  page uses, and it stays that way — the Google pages show
                  "Google" here and let the capsule name the product, but
                  "Which Google creatives produced this platform's delivery"
                  would name the company instead of the platform. */}
              <span className="ppx-hero-pill-label cphere-hero-pill-wordmark">
                {profile.heroDisplayLabel ?? profile.heroLabel}
              </span>
              <span className="ppx-hero-pill-tag cphere-hero-pill-tag">{profile.heroTag}</span>
            </div>
            <div className="ppx-hero-shimmer-bar" aria-hidden="true" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={resolvedAdvertiserName}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              loading={advertiserCatalogLoading}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={resolvedAdvertiserName}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        {/*
          The analytics workspace. Platform Analytics holds EDEN, the controls,
          the basis strip, the KPI strip, delivery and every deeper module
          inside one glowing surface; an individual platform page is that same
          page scoped to one platform, so it gets the same container rather
          than a column of disconnected panels on the page background.
        */}
        <div className="ppx-analytics-shell">
        {/* EDEN command bar — the same component Platform Analytics renders.
            Each platform profile keeps its own example query; the control it
            is typed into is identical on all of them. */}
        <AnalyticsAskEden
          value={edenInput}
          onChange={(event) => setEdenInput(event.target.value)}
          onSubmit={submitEdenCommand}
          placeholder={profile.edenPlaceholder}
          inputLabel={`Ask EDEN to reconfigure this ${profile.heroLabel} analytics view`}
          answer={edenAnswer}
          onDismissAnswer={() => setEdenAnswer(null)}
        />

        {/*
          Controls — the same vocabulary and order as Platform Analytics:
          campaign scope, date range, spend basis, comparison, Advanced, Reset.
          A control the reporting contract cannot honour is disabled in place
          with its reason, never dropped.
        */}
        <div className="ppx-filter-row" role="toolbar" aria-label="Analytics controls">
          <select
            className="ppx-select"
            value={campaignId}
            onChange={(event) => setCampaignId(event.target.value)}
            aria-label="Campaign scope"
          >
            <option value="">All campaigns</option>
            {campaignOptions.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaignSelectorLabel(campaign)}
              </option>
            ))}
          </select>

          <div className="ppx-chip-group" role="group" aria-label="Date range">
            {PLATFORM_WINDOW_PRESETS.map((preset) => (
              <React.Fragment key={preset.key}>
                <button
                  type="button"
                  className={classNames(
                    "ppx-chip",
                    effectiveRangeKey === preset.key && "ppx-chip--active"
                  )}
                  aria-pressed={effectiveRangeKey === preset.key}
                  onClick={() => setRangeKey(preset.key)}
                >
                  {preset.label}
                </button>
                {/* FULL DELIVERY rides immediately after CAMPAIGN FLIGHT, and
                    only when the delivery lifecycle actually outran the
                    original approved flight for this scope. */}
                {preset.key === DEFAULT_PLATFORM_WINDOW && fullDeliveryOffered ? (
                  <button
                    type="button"
                    className={classNames(
                      "ppx-chip",
                      effectiveRangeKey === FULL_DELIVERY_WINDOW_KEY && "ppx-chip--active"
                    )}
                    aria-pressed={effectiveRangeKey === FULL_DELIVERY_WINDOW_KEY}
                    onClick={() => setRangeKey(FULL_DELIVERY_WINDOW_KEY)}
                  >
                    {FULL_DELIVERY_WINDOW_LABEL}
                  </button>
                ) : null}
              </React.Fragment>
            ))}
          </div>

          <select
            className="ppx-select"
            value={resolvedWindow.comparisonSupported ? comparison : "none"}
            onChange={(event) => setComparison(event.target.value)}
            disabled={!resolvedWindow.comparisonSupported}
            title={resolvedWindow.comparisonUnsupportedReason || undefined}
            aria-label="Comparison period"
          >
            {PLATFORM_COMPARISON_MODES.map((mode) => (
              <option key={mode.key} value={mode.key}>
                {mode.label}
              </option>
            ))}
          </select>

          <select
            className="ppx-select ppx-select--small"
            value={viewModel.chart?.metricKey || ""}
            onChange={(event) => setMetricKey(event.target.value)}
            aria-label="Chart metric"
          >
            {profile.chartMetricKeys.map((key) => (
              <option key={key} value={key}>
                {resolvePlatformMetric(key)?.label || key}
              </option>
            ))}
          </select>

          <button
            type="button"
            className={classNames("ppx-chip ppx-chip--advanced", advancedOpen && "ppx-chip--active")}
            aria-expanded={advancedOpen}
            onClick={() => setAdvancedOpen((open) => !open)}
          >
            Advanced
          </button>

          <button
            type="button"
            className="ppx-chip"
            onClick={() => {
              setRangeKey(DEFAULT_PLATFORM_WINDOW);
              setCampaignId("");
              setComparison("prior_period");
              setMetricKey("");
              setFixtureState("");
              setEdenAnswer(null);
            }}
          >
            Reset
          </button>
        </div>

        {/* The FULL DELIVERY view states its own provenance: the original
            approved flight, then either the authorized extension or the
            governed admission that no authorization was recorded. It never
            implies the delivery inside the window was authorized. */}
        {effectiveRangeKey === FULL_DELIVERY_WINDOW_KEY && fullDeliveryProvenance ? (
          <p className="ppx-control-note" role="note" data-full-delivery="true">
            ORIGINAL APPROVED FLIGHT · {fullDeliveryProvenance.flightStartDate} –{" "}
            {fullDeliveryProvenance.flightEndDate}
            {fullDeliveryProvenance.authorizedDeliveryEndDate ? (
              <>
                {" "}
                · AUTHORIZED EXTENSION · THROUGH{" "}
                {fullDeliveryProvenance.authorizedDeliveryEndDate}
              </>
            ) : fullDeliveryProvenance.deliveryBeyondAuthorization ? (
              <> · EXTENSION AUTHORIZATION · NOT RECORDED</>
            ) : null}
          </p>
        ) : /* Two governed date contracts, both visible: the original approved
            flight is history; the authorized delivery window is the current
            execution authority after a Full Delivery extension. */
        deliveryExtensionProvenance ? (
          <p className="ppx-control-note" role="note" data-delivery-extended="true">
            Original approved flight {deliveryExtensionProvenance.flightStartDate} →{" "}
            {deliveryExtensionProvenance.flightEndDate} · Authorized delivery through{" "}
            {deliveryExtensionProvenance.authorizedDeliveryEndDate} (Full Delivery)
          </p>
        ) : null}

        {!resolvedWindow.comparisonSupported && (
          <p className="ppx-control-note" role="note">
            {resolvedWindow.comparisonUnsupportedReason}
          </p>
        )}

        {/* Client-safe reporting basis only; internal net-gate detail stays redacted. */}
        <p className="ppx-basis-strip">
          <span className="ppx-basis-strip-label">Basis: {viewModel.governance.basisLabel}</span>
        </p>

        {advancedOpen && (
          <div className="ppx-advanced-panel">
            <div className="ppx-advanced-field">
              <span className="ppx-advanced-label">Design preview</span>
              <select
                className="ppx-select"
                value={fixtureState}
                onChange={(event) => setFixtureState(event.target.value)}
                aria-label="Design preview state"
              >
                <option value="">Off — live reporting</option>
                {PLATFORM_ANALYTICS_FIXTURE_STATES.map((state) => (
                  <option key={state.key} value={state.key}>
                    {state.label}
                  </option>
                ))}
              </select>
              <p className="ppx-advanced-note">
                Governed design fixtures for review and testing. Live delivery is drawn by day
                wherever the reporting source reports by day, and as the window total wherever it
                does not — it is never interpolated.
              </p>
            </div>
          </div>
        )}

        {/* Source badge — always visible when data is not live reporting. */}
        {viewModel.sourceBadge && (
          <p className="ppx-source-badge">
            <span className="ppx-source-badge-icon" aria-hidden="true">◈</span>
            {viewModel.sourceBadge}
          </p>
        )}

        {/* States */}
        {!resolvedAdvertiserId && !fixtureState ? (
          <div className="ppx-state-panel">
            <h2 className="ppx-state-title">Choose an advertiser</h2>
            <p className="ppx-state-body">
              {profile.heroLabel} analytics resolves against a specific advertiser. Select one above — no
              metrics are shown outside your authorization scope.
            </p>
          </div>
        ) : (!fixtureState &&
            resolvedAdvertiserId &&
            !governedAnalyticsAdvertiserId) ||
          (loading && !hasResolved) ? (
          <div className="ppx-state-panel" role="status" data-analytics-state="loading">
            <h2 className="ppx-state-title">Resolving governed reporting…</h2>
            <p className="ppx-state-body">Loading {profile.heroLabel} metrics for {resolvedAdvertiserName || "the selected advertiser"}.</p>
          </div>
        ) : viewModel.state === "error" ? (
          <div className="ppx-state-panel" role="alert">
            <h2 className="ppx-state-title">Live metrics unavailable</h2>
            <p className="ppx-state-body">{viewModel.errorMessage}</p>
          </div>
        ) : viewModel.state === "no_data" ? (
          /* The governed lifecycle key is carried on the panel so the shared
             stylesheet can tell a scheduled campaign that has not started
             delivering from a window that simply returned nothing. */
          <div
            className="ppx-state-panel"
            data-analytics-state={
              selectedCampaignNoDelivery?.key || "no_reported_delivery"
            }
          >
            <h2 className="ppx-state-title">
              {selectedCampaignNoDelivery?.title || "No reported delivery"}
            </h2>
            <p className="ppx-state-body">
              {selectedCampaignNoDelivery?.message ||
                `There are no usable ${profile.heroLabel} metrics for this advertiser and analysis window. Nothing is estimated in their place.`}
            </p>
          </div>
        ) : null}

        {ready && (
          <>
            <p id="ppx-effective-reporting-window" className="ppx-control-note" data-reporting-window="effective">
              Reporting window: {viewModel.window?.label || "Not available"}
              {/* The calendar the response states — never the portal's default. */}
              {!fixtureState && reportingTimeZoneStatement(topline?.raw?.reportingTimeZone) ? (
                <span data-reporting-time-zone={topline.raw.reportingTimeZone}>
                  {" · "}{reportingTimeZoneStatement(topline.raw.reportingTimeZone)}
                </span>
              ) : !fixtureState && topline?.raw && Object.prototype.hasOwnProperty.call(topline.raw, "reportingTimeZone") ? (
                // The answer stated no calendar: say so, never assume one.
                <span data-calendar-not-stated="true">
                  {" · "}Calendar not stated: days as the source reported them
                </span>
              ) : null}
            </p>
            {!fixtureState && sourceCalendarQualification({ reportingCalendar: topline?.raw?.reportingCalendar }, { platforms: profile?.platformKey }) ? (
              <p className="ppx-control-note" data-source-calendar-qualification="true">
                {sourceCalendarQualification({ reportingCalendar: topline.raw.reportingCalendar }, { platforms: profile?.platformKey })}
              </p>
            ) : null}
            {viewModel.requestedWindow?.label && (
              <p className="ppx-control-note" data-reporting-window="requested">
                Requested window: {viewModel.requestedWindow.label}
              </p>
            )}
            {/* Command metric strip */}
            <div className="ppx-kpi-strip" role="group" aria-label="Topline metrics" aria-describedby="ppx-effective-reporting-window">
              {viewModel.commandMetrics.map((metric) => {
                const isSelected = metric.selectable && metric.key === viewModel.chart.metricKey;
                const body = (
                  <>
                    <span className="ppx-kpi-label">{metric.label}</span>
                    <span className="ppx-kpi-value ppx-numeric">
                      {formatPlatformMetricValue(metric.unit, metric.value, {
                        compact: metric.compact !== false,
                      })}
                    </span>
                    <span className="ppx-kpi-detail">
                      {metric.unavailableReason || metric.definition || " "}
                    </span>
                  </>
                );
                return metric.selectable ? (
                  <button
                    key={metric.key}
                    type="button"
                    className={classNames(
                      "ppx-kpi-card ppx-kpi-card--selectable",
                      isSelected && "ppx-kpi-card--active"
                    )}
                    aria-pressed={isSelected}
                    onClick={() => setMetricKey(metric.key)}
                  >
                    {body}
                  </button>
                ) : (
                  <div key={metric.key} className="ppx-kpi-card">
                    {body}
                  </div>
                );
              })}
            </div>

            {/*
              Hero chart panel.

              This panel was once dropped on a measurement page, because it
              could only say the daily contract was uncertified while GA4's own
              daily series was drawn by a second chart below — the page
              contradicting itself in consecutive modules. The fix was to give
              this chart the evidence rather than to hide it: the GA4 adapter
              now fills the same `dailyDelivery` key every media platform
              supplies, so one chart draws every source that reports by day and
              the duplicate below it is gone.
            */}
            <section className="ppx-panel ppx-panel--hero" aria-label="Delivery chart">
              <div className="ppx-panel-header">
                <div>
                  <h2 className="ppx-panel-title">Delivery</h2>
                  <p className="ppx-panel-subtitle">
                    {viewModel.window?.label || "Analysis window"} · {viewModel.governance.basisLabel}
                    {(viewModel.chart.performance || viewModel.chart.mode === "daily")
                      ? viewModel.sourceKind === "design_fixture"
                        ? " · daily (design preview)"
                        : ` · reported by day (${viewModel.chart.performance?.observedDays ?? viewModel.chart.observedDays} reported ${
                            (viewModel.chart.performance?.observedDays ?? viewModel.chart.observedDays) === 1 ? "day" : "days"
                          })`
                      : " · window total"}
                  </p>
                </div>
              </div>

              {viewModel.chart.available ? (
                <DeliveryChart
                  key={analyticsScopeKey}
                  chart={viewModel.chart}
                  accent={profile.accent}
                  platformKey={profile.platformKey}
                  platformLabel={profile.heroLabel}
                />
              ) : (
                <div className="ppx-state-panel ppx-state-panel--inline">
                  <p className="ppx-state-body">
                    {chartMetric?.label || "This metric"} is not reported for the current scope. It is shown
                    as unavailable rather than estimated.
                  </p>
                </div>
              )}

              {/*
                The disclosure states the reporting state this chart is
                actually in, so it is derived from the resolved series rather
                than fixed per platform: a source that reports by day says
                nothing here, and one that reports only a window total still
                says so.
              */}
              {viewModel.chart.disclosure && !viewModel.chart.performance && (
                <p className="ppx-chart-pending-note" role="note">
                  {viewModel.chart.disclosure}
                </p>
              )}

              <button
                type="button"
                className="ppx-describe-toggle"
                aria-expanded={describeOpen}
                onClick={() => setDescribeOpen((open) => !open)}
              >
                Describe this chart in words
              </button>
              {describeOpen && <p className="ppx-describe-text">{viewModel.chart.textSummary}</p>}
            </section>
          </>
        )}

        {/*
          Creative Performance sits directly beneath delivery, because the
          question it answers — which creatives produced that delivery — only
          makes sense once the delivery above it has been read.
        */}
        {!isGa4Measurement && creativeViewModel && (
          <CreativePerformancePanel
            viewModel={creativeViewModel}
            onSortChange={setCreativeSort}
          />
        )}

        {/*
          Platform-native deeper analytics.

          The live Meta ads library is where Facebook and Instagram keep their
          real reporting semantics: publisher identity, the separately-governed
          Audience Network and Messenger delivery, and the reconciliation that
          keeps those placements from inflating Facebook totals. Moving the
          component moves all of it — this page does not reimplement any of it.
        */}
        {profile.metaAdsLibrary && governedAnalyticsAdvertiserId && !fixtureState && (
          <LiveMetaAdsLibrary
            advertiserId={governedAnalyticsAdvertiserId}
            platformKey={profile.platformKey}
            platformLabel={profile.heroLabel}
            reportingSnapshot={metaReportingSnapshot}
          />
        )}

        {/*
          Inventory transparency is a capability these programmatic channels
          already had; adopting the shared shell preserves it rather than
          trading it for consistency.
        */}
        {profile.distributionTransparencyChannel && governedAnalyticsAdvertiserId && !fixtureState && (
          <DistributionTransparencyPanel
            advertiserId={governedAnalyticsAdvertiserId}
            campaignId={preferredCampaign?.id || null}
            platformKey={profile.distributionTransparencyChannel}
            platformLabel={profile.heroLabel}
            startDate={timeframe?.start || null}
            endDate={timeframe?.end || null}
            mode="client"
          />
        )}

        {/*
          GA4 measurement modules. Same components Campaign Analysis renders —
          imported from src/ga4Analytics, not reimplemented — so the property
          time zone, missing-is-not-zero and Google's age/gender thresholding
          behave identically on both surfaces.

          Traffic Over Time is deliberately NOT among them here. It drew the
          same GA4 days, for the same window, with the same four-metric
          selector as the Delivery chart above — two charts of one series,
          which is the duplication this workspace exists to prevent. Campaign
          Analysis still renders it from the shared module; this page reads
          those days through the canonical delivery contract instead.
        */}
        {isGa4Measurement && ga4AudienceDashboard
          ? renderGoogleAnalyticsAudienceDashboard(ga4AudienceDashboard)
          : null}

        {/*
          Connected report — informational state only. It says a governed
          report mapping exists for this advertiser; it does not offer to
          open it, because the reporting a viewer is meant to read is the
          native workspace on this page rather than a Looker Studio surface.
          Rendered only when a mapping actually exists: with no action
          attached, "no report is configured" is noise to a viewer who could
          not configure one anyway.
        */}
        {reportAvailable && (
          <section className="ppx-panel ppx-panel--report" aria-label="Connected report">
            <h2 className="ppx-panel-title">Connected report</h2>
            <p className="ppx-panel-subtitle">
              {profile.reportSourceLabel} is configured for {resolvedAdvertiserName || "this advertiser"}.
            </p>
            {profile.platformKey === "youtube" && topline?.raw ? (
              <p className="ppx-panel-source">
                Workbook: {topline.raw.sourceWorkbookTitle || profile.reportSourceLabel} ·
                Data through: {topline.raw.sourceDataThrough || "not reported"} ·
                Last successful read: {topline.raw.sourceLastReadAt || "not reported"} ·
                Snapshot: {topline.raw.sourceHash ? String(topline.raw.sourceHash).slice(0, 12) : "not reported"} ·
                Requested window: {topline.raw.requestedStartDate || "—"} → {topline.raw.requestedEndDate || "—"}
              </p>
            ) : null}
          </section>
        )}

        {/*
          Audience Data Synthesis — rendered independently of the metrics
          state: a governed synthesis can exist without analytics delivery,
          and delivery can exist without a synthesis. Each case renders its
          own honest state.
        */}
        {(governedAnalyticsAdvertiserId || fixtureState) && (
          <AudienceSynthesisPanel
            viewModel={audienceViewModel}
            profile={profile}
            role={AUDIENCE_SYNTHESIS_ROLE}
          />
        )}

        {ready && (
          <div className="ppx-intel-grid">
            {/* SHERLOC */}
            <section className="ppx-panel ppx-panel--sherloc" aria-label="SHERLOC intelligence">
              <div className="ppx-panel-header">
                <div>
                  <h2 className="ppx-panel-title">SHERLOC</h2>
                  <p className="ppx-panel-subtitle">Observed facts first, interpretation clearly separated</p>
                  {/*
                    * The source SHERLOC is reading, printed from the same
                    * resolution Pantheon prints. The two cards cannot name
                    * different sources, because there is only one resolver.
                    */}
                  <p className="ppx-panel-source" data-source-state={viewModel.sherlocSource?.state}>
                    Source · {viewModel.sherlocSource?.label || NOT_DECLARED}
                  </p>
                </div>
                <span className="ppx-panel-badge">Intelligence</span>
              </div>
              <ul className="ppx-sherloc-list">
                {viewModel.sherloc.map((insight, index) => (
                  <li key={index} className="ppx-sherloc-item">
                    <span className={`ppx-sherloc-kind ppx-sherloc-kind--${insight.kind}`}>
                      {insight.kind === "observed"
                        ? "Observed"
                        : insight.kind === "calculated"
                        ? "Calculated"
                        : insight.kind === "interpretation"
                        ? "Interpretation"
                        : "Recommendation"}
                    </span>
                    <p className="ppx-sherloc-text">{insight.text}</p>
                  </li>
                ))}
                {!viewModel.sherloc.length && (
                  <li className="ppx-sherloc-item">
                    <p className="ppx-sherloc-text">No notable observations for the selected scope.</p>
                  </li>
                )}
              </ul>
              <p className="ppx-sherloc-footer">
                Informational only. No execution path is exposed from this surface — nothing here can change
                a campaign.
              </p>
            </section>

            {/* Pantheon */}
            <section className="ppx-panel ppx-panel--pantheon" aria-label="Pantheon source facts">
              <div className="ppx-panel-header">
                <div>
                  <h2 className="ppx-panel-title">Pantheon</h2>
                  <p className="ppx-panel-subtitle">Whether this view is complete enough to decide on</p>
                </div>
              </div>
              {viewModel.pantheon.assessmentAvailable ? (
                <dl className="ppx-pantheon-facts">
                  <div>
                    <dt>Source</dt>
                    <dd>{viewModel.pantheon.sourceLabel || NOT_DECLARED}</dd>
                  </div>
                  <div>
                    <dt>Tier</dt>
                    <dd>{viewModel.pantheon.tier || NOT_DECLARED}</dd>
                  </div>
                  <div>
                    <dt>Freshness</dt>
                    <dd className="ppx-numeric">{formatFreshness(viewModel.pantheon.freshnessMinutes)}</dd>
                  </div>
                  <div>
                    <dt>Confidence</dt>
                    <dd className="ppx-numeric">
                      {viewModel.pantheon.confidence != null
                        ? `${Math.round(viewModel.pantheon.confidence * 100)}%`
                        : NOT_DECLARED}
                    </dd>
                  </div>
                  <div>
                    <dt>State</dt>
                    <dd>
                      {/*
                        * One canonical readiness state, resolved beside the one
                        * SHERLOC interprets. "Unresolved" is no longer reachable
                        * from an undeclared tier, confidence or freshness — only
                        * from a scope where nothing named a source and nothing
                        * usable was reported.
                        */}
                      <span
                        className={`ppx-status ppx-status--${viewModel.pantheon.statusTone}`}
                        data-source-state={viewModel.pantheon.state}
                      >
                        {viewModel.pantheon.statusLabel}
                      </span>
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="ppx-state-body">
                  No canonical Pantheon assessment exists for this scope — no score is invented in its place.
                </p>
              )}
              {viewModel.pantheon.assessmentAvailable &&
                (viewModel.pantheon.limitations || []).map((limitation, index) => (
                  <p key={index} className="ppx-pantheon-limitation" role="note">
                    ⚠ {limitation}
                  </p>
                ))}
            </section>
          </div>
        )}

        {/* end of the analytics workspace */}
        </div>
      </div>
    </div>
  );
}
